import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useBilling } from "../contexts/BillingContext";
import { setWorkLabel as persistWorkLabel } from "../lib/workLabel";
import { emitGeofenceSettingChange } from "../components/GeofenceController";
import { ensureNotificationPermission } from "../lib/geofenceWatcher";
import SectionToggle from "../components/SectionToggle";
import PushNotificationSection from "../components/PushNotificationSection";
import RedeemInviteCode from "../components/RedeemInviteCode";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Crown, Check, Briefcase, Bell, Volume2, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";

const WORK_LABEL_PRESETS = ["Work", "Restaurant Ops", "Retail Ops", "Field Ops", "Studio"];

export default function SettingsPage() {
  const { user, updateProfile, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { status: billing, syncSession, refresh: refreshBilling } = useBilling();
  const [name, setName] = useState(user?.name || "");
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch {}
    })();
  }, []);

  // Handle Stripe Checkout redirect (?upgrade=success&session_id=...)
  useEffect(() => {
    const upgrade = params.get("upgrade");
    const sessionId = params.get("session_id");
    if (upgrade === "success" && sessionId) {
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const data = await syncSession(sessionId);
          if (data?.is_pro) {
            toast.success("You're on Pro — thanks for your support!");
            navigate("/settings", { replace: true });
            return;
          }
        } catch {}
        if (attempts < 6) {
          setTimeout(poll, 1500);
        } else {
          toast.message("Payment is being processed. Check back in a moment.");
        }
      };
      poll();
    } else if (upgrade === "cancelled") {
      toast.message("Upgrade cancelled — no charge made.");
      navigate("/settings", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setName(user?.name || "");
  }, [user]);

  const saveProfile = async () => {
    setSaving(true);
    const res = await updateProfile({ name });
    setSaving(false);
    if (res.ok) toast.success("Profile updated");
    else toast.error(res.error || "Update failed");
  };

  const setSetting = async (key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
    try {
      await api.put("/settings", { [key]: value });
    } catch {
      toast.error("Failed to save");
    }
  };

  const setWorkLabelValue = async (value) => {
    setSettings((s) => ({ ...s, work_label: value }));
    await persistWorkLabel(value);
    toast.success(
      value ? `Work workspace renamed to "${value}"` : "Work workspace label reset"
    );
  };

  return (
    <div className="px-4 sm:px-8 py-8 max-w-3xl mx-auto">
      <div className="mb-7 anim-fade-up">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Account</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Settings</h1>
      </div>

      <div className="space-y-5">
        {/* Profile */}
        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-display font-bold mb-4 text-sm uppercase tracking-wider">Profile</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full name</Label>
              <Input
                data-testid="settings-name-input"
                className="mt-1.5"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input
                data-testid="settings-email-input"
                className="mt-1.5"
                value={user?.email || ""}
                disabled
              />
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={saveProfile} disabled={saving} data-testid="settings-save-profile-btn">
              {saving ? "Saving…" : "Save profile"}
            </Button>
          </div>
        </section>

        {/* Appearance */}
        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-display font-bold mb-4 text-sm uppercase tracking-wider">Appearance</h2>
          <div className="flex items-center justify-between py-2">
            <div>
              <div className="text-sm font-medium">Theme</div>
              <div className="text-xs text-muted-foreground">Sync your preference across devices</div>
            </div>
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger className="w-32" data-testid="settings-theme-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="light">Light</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* Preferences */}
        {settings && (
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-display font-bold mb-4 text-sm uppercase tracking-wider">Preferences</h2>
            <div className="space-y-2.5">
              <ToggleRow
                icon={<Bell size={13} strokeWidth={2} />}
                label="Notifications"
                description="Enable reminders for due dates"
                checked={!!settings.notifications_enabled}
                onToggle={() =>
                  setSetting("notifications_enabled", !settings.notifications_enabled)
                }
                testId="settings-notifications-switch"
              />
              <ToggleRow
                icon={<Volume2 size={13} strokeWidth={2} />}
                label="Sound"
                description="Play sound when a notification arrives"
                checked={!!settings.sound_enabled}
                onToggle={() => setSetting("sound_enabled", !settings.sound_enabled)}
                testId="settings-sound-switch"
              />
              <ToggleRow
                icon={<Mail size={13} strokeWidth={2} />}
                label="Weekly digest"
                description="Receive a weekly summary email"
                checked={!!settings.weekly_digest}
                onToggle={() => setSetting("weekly_digest", !settings.weekly_digest)}
                testId="settings-digest-switch"
              />
              <ToggleRow
                icon={<MapPin size={13} strokeWidth={2} />}
                label="Geofence alerts"
                description="Get a ping when you arrive at or leave a task's pinned location (foreground only, while the app is open)"
                checked={!!settings.geofence_alerts_enabled}
                onToggle={async () => {
                  const nextValue = !settings.geofence_alerts_enabled;
                  await setSetting("geofence_alerts_enabled", nextValue);
                  emitGeofenceSettingChange(nextValue);
                  if (nextValue) {
                    const perm = await ensureNotificationPermission();
                    if (perm !== "granted") {
                      toast.message(
                        "Browser notifications are off — you'll still see in-app alerts."
                      );
                    }
                  }
                }}
                testId="settings-geofence-switch"
              />
            </div>
            <div className="mt-4">
              <Row
                title="Default dashboard"
                subtitle="Where you land on sign-in"
                control={
                  <Select value={settings.default_dashboard} onValueChange={(v) => setSetting("default_dashboard", v)}>
                    <SelectTrigger className="w-36" data-testid="settings-default-dashboard">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal">Personal</SelectItem>
                      <SelectItem value="family">Family</SelectItem>
                      <SelectItem value="work">Work</SelectItem>
                      <SelectItem value="school">School</SelectItem>
                    </SelectContent>
                  </Select>
                }
              />
            </div>
          </section>
        )}

        {/* Push Notifications */}
        {settings && (
          <PushNotificationSection settings={settings} setSetting={setSetting} />
        )}

        {/* Workspace branding */}
        {settings && (
          <section
            data-testid="settings-workspace-branding"
            className="rounded-xl border border-border bg-card p-5"
          >
            <h2 className="font-display font-bold mb-1 text-sm uppercase tracking-wider flex items-center gap-2">
              <Briefcase size={14} className="text-primary" /> Workspace branding
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Rename the Work workspace so it matches your business — handy for restaurant ops,
              retail, studios, or field teams. Affects sidebar, headers, and task buttons.
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Work workspace label
                </Label>
                <Input
                  data-testid="settings-work-label-input"
                  placeholder="e.g. Restaurant Ops"
                  value={settings.work_label || ""}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, work_label: e.target.value }))
                  }
                  onBlur={(e) => setWorkLabelValue(e.target.value)}
                  className="mt-1.5 max-w-xs"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Leave blank to use the default label "Work".
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {WORK_LABEL_PRESETS.map((preset) => {
                  const isDefault = preset === "Work";
                  const value = isDefault ? "" : preset;
                  const isActive = (settings.work_label || "") === value;
                  return (
                    <button
                      key={preset}
                      type="button"
                      data-testid={`settings-work-label-preset-${preset.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setWorkLabelValue(value)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${
                        isActive
                          ? "border-primary text-primary bg-primary/10"
                          : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                      }`}
                    >
                      {preset}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Subscription */}
        <section data-testid="settings-billing-section" className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-display font-bold mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
            <Crown size={14} className="text-primary" /> Subscription
          </h2>
          {billing?.is_pro ? (
            <div>
              <div className="inline-flex items-center gap-2 text-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-500 rounded-full px-3 py-1 mb-3">
                <Check size={13} /> {billing.subscription_status === "trialing" ? "Pro trial active" : `Pro · ${billing.plan}`}
              </div>
              {billing.current_period_end && (
                <p className="text-xs text-muted-foreground">
                  {billing.subscription_status === "trialing" ? "Trial ends" : "Pro access until"}{" "}
                  <span className="text-foreground">
                    {new Date(billing.current_period_end).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                  {billing.subscription_status === "trialing" && billing.trial_remaining_days !== undefined && (
                    <span> · {billing.trial_remaining_days} day{billing.trial_remaining_days === 1 ? "" : "s"} left</span>
                  )}
                </p>
              )}
              <div className="mt-4 flex gap-2">
                <Button variant="outline" onClick={() => navigate("/pricing")} data-testid="settings-manage-plan-btn">
                  Manage plan
                </Button>
                <Button variant="ghost" onClick={refreshBilling} data-testid="settings-refresh-billing-btn">
                  Refresh
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground mb-4">
                You're on the <span className="text-foreground font-medium">Free</span> plan.
                {billing?.limits?.task_limit
                  ? ` Up to ${billing.limits.task_limit} active tasks and ${billing.limits.family_invite_limit} family invite.`
                  : ""}
              </p>
              <Button onClick={() => navigate("/pricing")} data-testid="settings-upgrade-btn">
                <Crown size={14} className="mr-1.5" /> Upgrade to Pro
              </Button>
            </div>
          )}
        </section>

        {/* Invite code redemption */}
        <section
          data-testid="settings-redeem-section"
          className="rounded-xl border border-border bg-card p-5"
        >
          <h2 className="font-display font-bold mb-1 text-sm uppercase tracking-wider">
            Promo / Invite Code
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            Have a code from our team? Redeem it for extra task quota or free
            Pro time. Codes are case-insensitive.
          </p>
          <RedeemInviteCode compact />
          {user?.role === "admin" && (
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/invite-codes")}
                data-testid="settings-manage-invite-codes-btn"
              >
                Manage invite codes
              </Button>
            </div>
          )}
        </section>

        {/* Danger */}
        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-display font-bold mb-2 text-sm uppercase tracking-wider">Session</h2>
          <p className="text-xs text-muted-foreground mb-4">Sign out of FlowTasks360 on this device. Your cloud data stays safe.</p>
          <Button variant="destructive" onClick={logout} data-testid="settings-logout-btn">Sign out</Button>
        </section>

        <p className="text-[10px] text-muted-foreground text-center pt-4">
          © 2026 360DMA • Designed & Engineered by 360 Digital Marketing
        </p>
      </div>
    </div>
  );
}

function Row({ title, subtitle, control }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
      {control}
    </div>
  );
}

function ToggleRow({ icon, label, description, checked, onToggle, testId, accent }) {
  return (
    <div
      className={
        "rounded-lg border px-3 py-3 transition-colors " +
        (checked
          ? "border-primary/50 bg-primary/[0.04]"
          : "border-border bg-secondary/20")
      }
    >
      <SectionToggle
        icon={icon}
        label={label}
        checked={checked}
        onToggle={onToggle}
        accent={accent || "hsl(var(--primary))"}
        testId={testId}
        ariaLabel={label}
      />
      {description && (
        <p className="text-[11px] text-muted-foreground mt-1.5 ml-9 leading-relaxed">
          {description}
        </p>
      )}
    </div>
  );
}
